<template>
  <div class="flex w-full py-4 divide-x-2">
    <section class="w-[375px] flex-none px-4">
      <!-- Metrics selection summary -->
      <div class="sticky top-0 flex h-[40px] w-full items-center justify-between text-sm">
        <div class="flex items-center gap-1">
          <span
            v-if="selectedMetrics.length"
            class="flex items-center justify-center text-white rounded-full size-6 bg-main-primary"
          >
            <CheckRoundedSmall
              bg-color="bg-main-primary"
              custom-style="size-3.5"
            />
          </span>
          <span
            v-else
            class="flex h-6 w-6 items-center justify-center rounded-full bg-main-primary px-1.5 py-0.5 text-xs text-white"
          >1</span>
          <span class="font-medium text-gray-700 whitespace-nowrap">Step 1 - Select the Metrics</span>
        </div>
        <span class="text-xs whitespace-nowrap text-main-primary/70">{{ selectedMetrics.length }} Metrics
          <span class="text-gray-700">Selected</span></span>
      </div>
      <!-- Topics with metrics -->
      <div class="mt-4 flex min-h-screen flex-col gap-y-2.5">
        <EsgTopicMetricsAssignAccordion
          v-for="{ id, title, metrics } in topicMetrics"
          :ref="`assign-accordion-${id}`"
          :key="id"
          :topic="title"
          :metrics
          :saved-metrics="[...metrics].filter(({ id }) => savedMetrics.includes(id))
          "
          @metric-deselected="handleMetricDeselected"
          @metrics-selected="handleMetricsSelected"
          @panel-opened="closeOtherPanels(id)"
        />
      </div>
    </section>

    <section
      class="max-h-screen max-w-[calc(100%-375px)] grow overflow-auto px-4"
      :class="{ 'pointer-events-none opacity-40': !selectedMetrics.length }"
    >
      <EsgAssignableAssetsTree
        :ref="`assets-tree-${uid}`"
        :key="uid"
        :saved-metrics="savedMetrics"
        :saved-assets="savedAssets"
        @assets-selected="handleAssetsSelected"
      />
    </section>

    <div
      v-if="selectedAssets.length"
      class="fixed right-0 top-[100dvh] z-50 flex w-full -translate-y-full items-center justify-end gap-x-6 bg-white py-6 pr-4 transition-all sm:pr-6 lg:pr-8"
    >
      <div class="flex items-center gap-1">
        <span
          class="flex size-6 items-center justify-center rounded-full bg-main-primary px-1.5 py-0.5 text-xs text-white"
        >3</span>
        <span class="font-medium text-gray-700 whitespace-nowrap">Step 3 - Assign allUsers to metrics</span>
      </div>
      <button
        id="step3-cta"
        type="button"
        aria-label="Assign metrics and assets"
        class="brand-button"
        @click="showAssignModal"
      >
        Assign Metrics
      </button>
    </div>

    <ModalComponent
      name="assign-metrics"
      :shift-x="0.97"
      height="auto"
      :adaptive="true"
      :scrollable="true"
      :click-to-close="true"
      @closed="hideAssignModal"
    >
      <section class="p-6 border-b">
        <div class="flex items-center justify-between">
          <div>
            <h3 class="text-lg text-primary lg:text-xl">
              Assign Approval Workflow
            </h3>
            <p class="text-[#68737D]">
              Please fill all required fields to assign the approval workflow
            </p>
          </div>
          <button
            type="button"
            class="p-1 text-gray-400 bg-gray-200 rounded-md"
            @click="hideAssignModal"
          >
            <TimesSmall />
          </button>
        </div>
      </section>
      <!-- Form -->
      <form @submit.prevent="assignApprovalWorkflow">
        <div class="flex flex-col gap-4 p-6 mt-1 mb-6">
          <div>
            <label
              for="Assets"
              class="flex justify-start mb-2"
            >
              <span class="text-sm font-semibold">Assets</span>
            </label>
            <div class="flex flex-wrap items-start w-full gap-3">
              <span
                v-for="asset in selectedAssets"
                :key="asset.id"
                class="whitespace-nowrap rounded-md border border-[#ECFDF3] bg-[#ECFDF3] px-5 py-1.5 text-sm capitalize text-[#027A48]"
              >{{
                asset.asset_name }}</span>
            </div>
          </div>
          <div>
            <label
              for="topic"
              class="flex justify-start mb-2"
            >
              <span class="text-sm font-semibold">Topic</span>
            </label>
            <div class="flex flex-wrap items-start w-full gap-3">
              <span
                v-for="{ id, title } in selectedTopics"
                :key="`topic-${id}`"
                class="whitespace-nowrap rounded-md border border-[#E1EFFE] bg-[#E1EFFE] px-5 py-1.5 text-sm font-medium capitalize text-[#1E429F]"
              >
                {{ title }}</span>
            </div>
          </div>
          <div>
            <label
              for="Metrics"
              class="flex justify-start mb-2"
            >
              <span class="text-sm font-semibold">Metrics</span>
            </label>
            <div class="flex flex-wrap items-start w-full gap-3">
              <span
                v-for="{ id, name } in selectedMetricObjects"
                :key="`metric-${id}`"
                class="whitespace-nowrap rounded-md border border-[#EDEBFE] bg-[#EDEBFE] px-5 py-1.5 text-sm font-medium capitalize text-[#5521B5]"
              >
                {{ name }}</span>
            </div>
          </div>
          <div>
            <label class="flex justify-start mb-2">
              <span class="text-sm font-semibold">Data Owner</span>
              <span class="px-1 text-red-500">*</span>
            </label>
            <MultiSelector
              :options="allUsers"
              placeholder-text="Select the data owner(s)"
              display-prop="name"
              value-prop="id"
              @values-updated="updateOwners"
            />
            <small
              v-if="showEmptyOwnersErrorMessage"
              class="block mt-1 mb-2 font-medium text-red-primary"
            >'Data Owner'
              cannot be blank</small>
          </div>
          <div class="w-full group">
            <div class="mb-2 flex items-center gap-3.5">
              <div class="flex items-center">
                <label
                  for="select-all-topics-checkbox"
                  class="text-sm font-semibold"
                >Approver required?</label>
                <HelperText
                  title="Click on the checkbox if you require an approver to approve the submissions. Otherwise, submissions by the data owner will be automatically approved"
                />
              </div>
              <input
                id="approver-checkbox"
                v-model="is_approver_required"
                type="checkbox"
                class="p-2 bg-white border-2 border-gray-400 rounded-sm cursor-pointer text-main-primary hover:border-gray-500 focus:border-main-primary focus:ring-transparent"
              >
            </div>
          </div>
          <div v-if="is_approver_required">
            <div>
              <label class="flex justify-start mb-2 gap-x-1">
                <span class="text-sm font-semibold">Approver </span>
                <span class="px-1 text-red-500">*</span>
              </label>
              <MultiSelector
                v-if="updatedApprovers && selectedApprovers"
                :options="listOfUserApprovers"
                :options-to-edit="selectedApprovers"
                placeholder-text="Select the data approver(s)"
                display-prop="name"
                value-prop="id"
                @values-updated="updateApprovers"
              />
              <MultiSelector
                v-else
                :options="listOfUserApprovers"
                placeholder-text="Select the data approver(s)"
                display-prop="name"
                value-prop="id"
                @values-updated="updateApprovers"
              />
              <small
                v-if="showEmptyApproversErrorMessage"
                class="block mt-1 mb-2 font-medium text-red-primary"
              >'Data
                Approver' cannot be blank</small>
            </div>
            <div class="group">
              <div class="my-2 flex items-center gap-3.5 pt-2">
                <div class="flex items-center">
                  <label
                    for="first-to-respond-checkbox"
                    class="text-sm font-semibold"
                  >All Approvers Required?</label>
                  <HelperText
                    title="By default, Veridis requires all the approvers to approve. Uncheck the checkbox if you only require one approver to approve"
                  />
                </div>
                <input
                  id="respond-checkbox"
                  v-model="everyone_to_respond"
                  type="checkbox"
                  class="p-2 bg-white border-2 border-gray-400 rounded-sm cursor-pointer text-main-primary hover:border-gray-500 focus:border-main-primary focus:ring-transparent"
                >
              </div>
            </div>
          </div>

          <div class="flex flex-col gap-1">
            <label class="flex justify-start mb-2 gap-x-1">
              <span class="text-sm font-semibold">Data Upload Frequency </span>
              <span class="px-1 text-red-500">*</span>
            </label>
            <DropDownInput
              display-key="name"
              value-key="id"
              :options="frequencies"
              :first-value="data_collection_frequency"
              max-width-class="max-w-full"
              @value-updated="updateDataFrequency"
            />
            <small
              v-if="showEmptyDataFrequencyErrorMessage"
              class="block mt-1 mb-2 font-medium text-red-primary"
            >Data
              upload frequency is a required field</small>
          </div>
        </div>
        <div class="py-4 mt-12 mb-6 border-t">
          <div class="flex items-center justify-end gap-2 px-6">
            <GhostButton
              type="button"
              aria-label="Cancel"
              class="!px-6 !py-2"
              @click="hideAssignModal"
            >
              Cancel
            </GhostButton>
            <PrimaryButton
              type="submit"
              aria-label="Save"
              class="!px-6 !py-2"
              :disabled="!selectedMetrics.length
                || showEmptyOwnersErrorMessage
                || showEmptyApproversErrorMessage
                || showEmptyDataFrequencyErrorMessage
              "
            >
              Save
            </PrimaryButton>
          </div>
        </div>
      </form>
    </ModalComponent>
  </div>
</template>

<script>
import { usersInjectionKey } from '@/utils/keys'

export default {
  name: 'EsgThemeAssignContainer',

  inject: { usersInjectionKey },

  provide() {
    return {
      themeIsGhg: computed(() => this.themeIsGhg),
    }
  },

  props: {
    topics: {
      type: Array,
      required: true,
    },
    users: {
      type: Array,
      default() {
        return []
      },
    },
    esgFrequency: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['workflowSaved'],

  data() {
    return {
      uid: null,
      topicMetrics: this.topics,
      esgFrequencyType: this.esgFrequency,
      updatedApprovers: [],
      updatedDataOwners: [],
      selectedTopics: [],
      selectedAssets: [],
      selectedMetrics: [],
      listOfUserApprovers: [],
      selectedMetricObjects: [],
      selectedApprovers: [],
      showEmptyApproversErrorMessage: false,
      showEmptyDataFrequencyErrorMessage: false,
      showEmptyOwnersErrorMessage: false,
      data_collection_frequency: null,
      is_approver_required: false,
      everyone_to_respond: true,
      frequencies: [
        { name: 'Weekly', id: 'weekly' },
        { name: 'Monthly', id: 'monthly' },
        { name: 'Quarterly', id: 'quarterly' },
        { name: 'Semi-annually', id: 'semi-annually' },
        { name: 'Annually', id: 'annually' },
      ],
    }
  },

  computed: {
    allUsers() {
      return this.users.length ? this.users : this.usersInjectionKey
    },
    savedMetrics() {
      const selected = []

      this.topicMetrics.forEach(({ metrics }) => {
        metrics.forEach(({ id, data_owners }) => {
          if (!selected.includes(id) && data_owners.length) {
            selected.push(id)
          }
        })
      })

      return selected
    },
    savedAssets() {
      const selected = []

      this.topicMetrics.forEach(({ metrics }) => {
        metrics.forEach(({ assets }) => {
          assets.forEach((item) => {
            if (!selected.includes(item)) {
              selected.push(item)
            }
          })
        })
      })

      return selected
    },
    themeIsGhg() {
      return this.topics.some(({ title }) =>
        title.toLowerCase().includes('scope'),
      )
    },
  },

  watch: {
    topics(newTopicMetrics) {
      this.topicMetrics = newTopicMetrics
    },
    esgFrequency(newFrequency) {
      this.esgFrequencyType = newFrequency
    },
  },

  mounted() {
    this.uid = this.$.uid
  },

  methods: {
    handleMetricsSelected(metrics) {
      metrics.forEach((metric) => {
        if (!this.selectedMetrics.includes(metric)) {
          this.selectedMetrics.push(metric)
        }
      })
    },
    handleMetricDeselected(metric) {
      const deleteIndex = this.selectedMetrics.findIndex(
        item => item === metric,
      )

      if (deleteIndex !== -1) {
        this.selectedMetrics.splice(deleteIndex, 1)

        if (!this.selectedMetrics.length) {
          // Deselect assets when all metrics are back to zero
          this.$refs[`assets-tree-${this.uid}`].deselectAssets()
        }
      }
    },
    closeOtherPanels(topicMetricsId) {
      setTimeout(() => {
        this.topicMetrics.forEach(({ id }) => {
          if (id !== topicMetricsId) {
            this.$refs[`assign-accordion-${id}`][0].hidePanel()
          }
        })
      }, 100)
    },
    updateApprovers(allUsers) {
      this.showEmptyApproversErrorMessage = false
      this.updatedApprovers = allUsers
    },
    updateDataFrequency(newData) {
      this.showEmptyDataFrequencyErrorMessage = false
      this.data_collection_frequency = newData
    },
    updateOwners(allUsers) {
      this.updatedDataOwners = allUsers
      this.showEmptyOwnersErrorMessage = false
      this.showEmptyApproversErrorMessage = false
      this.listOfUserApprovers = this.allUsers.filter(
        ({ id }) => !allUsers.includes(id),
      )
      this.updateApprovers(
        this.updatedApprovers.filter(item => !allUsers.includes(item)),
      )
      this.selectedApprovers = this.listOfUserApprovers.filter(user =>
        this.updatedApprovers.includes(user.id),
      )
    },
    listTopicsFromMetrics(metricIds) {
      return [...this.topicMetrics]
        .filter(({ metrics }) =>
          metrics.some(({ id }) => metricIds.includes(id)),
        )
        .map(({ id, title }) => ({ id, title }))
    },
    assignApprovalWorkflow() {
      if (!this.updatedDataOwners.length) {
        this.showEmptyOwnersErrorMessage = true
        return
      }
      if (this.is_approver_required && !this.updatedApprovers.length) {
        this.showEmptyApproversErrorMessage = true
        return
      }
      if (!this.data_collection_frequency) {
        this.showEmptyDataFrequencyErrorMessage = true
        return
      }
      const selectedData = {
        data_owners: this.updatedDataOwners,
        approvers: this.is_approver_required ? this.updatedApprovers : [],
        assets: [...this.selectedAssets].map(({ id }) => id),
        data_collection_frequency: this.data_collection_frequency,
        is_approver_required: this.is_approver_required,
        everyone_to_respond: this.everyone_to_respond,
        ...(this.themeIsGhg && { categories: this.selectedMetrics }),
        ...(!this.themeIsGhg && {
          metrics: this.selectedMetrics,
          topics: [...this.selectedTopics].map(({ id }) => id),
        }),
      }
      const cid = localStorage.getItem('cid')

      this.$apis.esgMetrics
        .assignApprovalWorkflow(cid, selectedData)
        .then(({ data }) => {
          this.$toast.success(data.message, {
            timeout: 2000,
            position: 'top-right',
          })
          this.hideAssignModal()
          this.topicMetrics.forEach(({ id }) =>
            this.$refs[`assign-accordion-${id}`][0].deselectMetrics(),
          )
          this.selectedMetrics = []
          this.$refs[`assets-tree-${this.uid}`].deselectAssets()
          this.$emit('workflowSaved')
        })
    },
    hideAssignModal() {
      this.$modal.hide('assign-metrics')
      this.selectedTopics = []
      this.showEmptyOwnersErrorMessage = false
      this.showEmptyApproversErrorMessage = false
      this.selectedMetricObjects = []
      this.updatedApprovers = []
      this.updatedDataOwners = []
      this.listOfUserApprovers = []
      this.selectedApprovers = []
      this.data_collection_frequency = null
    },
    showAssignModal() {
      this.showEmptyOwnersErrorMessage = false
      this.showEmptyApproversErrorMessage = false
      this.selectedTopics = this.listTopicsFromMetrics(this.selectedMetrics)
      this.listOfUserApprovers = this.allUsers
      this.selectedApprovers = []
      this.data_collection_frequency = !this.esgFrequencyType
        ? 'monthly'
        : 'annually'
      let result = []
      this.frequencies = !this.esgFrequencyType
        ? [...this.frequencies]
        : [...this.frequencies].filter(({ id }) => id !== 'weekly')
      this.topicMetrics.forEach(({ metrics }) => {
        const matchingMetrics = [...metrics]
          .filter(({ id }) => this.selectedMetrics.includes(id))
          .map(({ id, name }) => ({ id, name }))

        result = [...result, ...matchingMetrics]
      })

      this.selectedMetricObjects = result
      this.$modal.show('assign-metrics')
    },
    handleAssetsSelected(assets) {
      this.selectedAssets = assets
    },
  },
}
</script>
